/**
 * API service module with typed fetch wrappers for the Payslip Audit backend.
 * Handles authentication token injection from Amplify and error parsing.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * File metadata sent with the upload initiation request. Field names match
 * what the Upload Lambda's validator reads, so adjusting these names will
 * break the wire contract - keep them in sync with
 * `packages/lambdas/upload/src/validator.ts`.
 */
export interface FileMetadata {
  /** Original filename (for diagnostics; not used for S3 keys). */
  name: string;
  /** MIME type, e.g. 'application/pdf', 'image/png', 'image/jpeg'. */
  mimeType: string;
  /** File size in bytes. */
  sizeBytes: number;
  /**
   * Page count. The browser cannot read PDF page counts without a parser,
   * so callers should default to 1 - the authoritative page check happens
   * server-side at extraction time via Textract.
   */
  pageCount: number;
}

/** Response from POST /upload/initiate */
export interface InitiateUploadResponse {
  auditId: string;
  payslipUploadUrl: string;
  rosterUploadUrls: string[];
}

/** Response from POST /upload/complete */
export interface CompleteUploadResponse {
  auditId: string;
  status: string;
}

/** Structured API error */
export interface ApiError {
  code: string;
  message: string;
  retryable: boolean;
  details?: string;
}

export class ApiRequestError extends Error {
  public readonly code: string;
  public readonly retryable: boolean;
  public readonly details?: string;
  public readonly statusCode: number;

  constructor(error: ApiError, statusCode: number) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.code = error.code;
    this.retryable = error.retryable;
    this.details = error.details;
    this.statusCode = statusCode;
  }
}

/**
 * Retrieves the current user's ID token from the Amplify auth session.
 * Throws if no valid session exists.
 */
async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new ApiRequestError(
      {
        code: 'AUTH_TOKEN_MISSING',
        message: 'Your session has expired. Please sign in again.',
        retryable: false,
      },
      401
    );
  }

  return token;
}

/**
 * Parses a non-OK response into a structured ApiError. Attempts to read a
 * JSON error body; falls back to the provided defaults when the body is
 * missing or unparseable.
 */
async function parseErrorResponse(
  response: Response,
  fallback: { code: string; message: string; retryable: boolean }
): Promise<never> {
  let apiError: ApiError;
  try {
    const body = await response.json();
    apiError = body.error ?? {
      code: fallback.code,
      message: fallback.message,
      retryable: fallback.retryable,
    };
  } catch {
    apiError = {
      code: fallback.code,
      message: fallback.message,
      retryable: fallback.retryable,
    };
  }
  throw new ApiRequestError(apiError, response.status);
}

/**
 * Generic typed fetch wrapper that injects auth and handles errors.
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string> | undefined),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    await parseErrorResponse(response, {
      code: 'NETWORK_ERROR',
      message: 'Unable to reach the server. Please check your connection.',
      retryable: true,
    });
  }

  return response.json() as Promise<T>;
}

/**
 * POST /upload/initiate
 * Requests presigned S3 URLs for payslip and roster document uploads.
 */
export async function initiateUpload(
  payslipMeta: FileMetadata,
  rosterMetas: FileMetadata[],
  retainData: boolean
): Promise<InitiateUploadResponse> {
  return apiRequest<InitiateUploadResponse>('/upload/initiate', {
    method: 'POST',
    body: JSON.stringify({
      payslip: payslipMeta,
      rosters: rosterMetas,
      retainData,
    }),
  });
}

/**
 * Uploads a file directly to S3 using a presigned PUT URL.
 * Uses XMLHttpRequest to support progress tracking.
 * Returns a promise that resolves when the upload completes.
 */
export function uploadFileToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new ApiRequestError(
            {
              code: 'S3_UPLOAD_FAILED',
              message: 'Failed to upload the file. Please try again.',
              retryable: true,
            },
            xhr.status
          )
        );
      }
    });

    xhr.addEventListener('error', () => {
      reject(
        new ApiRequestError(
          {
            code: 'NETWORK_ERROR',
            message: 'Upload failed due to a network error. Please check your connection and try again.',
            retryable: true,
          },
          0
        )
      );
    });

    xhr.addEventListener('abort', () => {
      reject(
        new ApiRequestError(
          {
            code: 'UPLOAD_ABORTED',
            message: 'Upload was cancelled.',
            retryable: true,
          },
          0
        )
      );
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

/**
 * POST /upload/complete
 * Signals that both files have been uploaded to S3 and triggers processing.
 */
export async function completeUpload(
  auditId: string
): Promise<CompleteUploadResponse> {
  return apiRequest<CompleteUploadResponse>('/upload/complete', {
    method: 'POST',
    body: JSON.stringify({ auditId }),
  });
}

// --- Retention settings ---

export interface RetentionPreferenceResponse {
  userId: string;
  retainData: boolean;
  consentTimestamp?: string;
  revokedTimestamp?: string;
}

/**
 * GET /settings/retention
 * Returns the current retention preference for the authenticated user.
 */
export async function getRetentionPreference(): Promise<RetentionPreferenceResponse> {
  return apiRequest<RetentionPreferenceResponse>('/settings/retention', {
    method: 'GET',
  });
}

/**
 * PUT /settings/retention
 * Updates the user's retention preference.
 */
export async function updateRetentionPreference(
  retainData: boolean
): Promise<RetentionPreferenceResponse> {
  return apiRequest<RetentionPreferenceResponse>('/settings/retention', {
    method: 'PUT',
    body: JSON.stringify({ retainData }),
  });
}

// --- Audit history ---

export interface HistoryReport {
  auditId: string;
  periodStart: string;
  periodEnd: string;
  hasDiscrepancies: boolean;
  createdAt: string;
}

/**
 * GET /audit/history
 * Returns up to 52 retained audit reports for the authenticated user.
 */
export async function getAuditHistory(): Promise<HistoryReport[]> {
  return apiRequest<HistoryReport[]>('/audit/history', {
    method: 'GET',
  });
}

// --- Data export ---

/**
 * POST /account/data-export
 * Initiates a data export and returns the response as a Blob for download.
 * Uses a separate path from apiRequest because the response is a file, not JSON.
 */
export async function exportUserData(): Promise<Blob> {
  const token = await getAuthToken();
  const url = `${API_BASE_URL}/account/data-export`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    await parseErrorResponse(response, {
      code: 'EXPORT_FAILED',
      message: 'Failed to export your data. Please try again.',
      retryable: true,
    });
  }

  return response.blob();
}
